import React, { useMemo, useRef, useState, useEffect, useContext } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react';
import { Autoplay, Pagination } from 'swiper';

import { AuthContext, capitalize } from '@repo-level/core-front';

import { createItems } from 'helpers/common';
import { useChunks } from 'hooks/chunks';
import { CustomParameters, getCustomProps, SwiperProps } from './helpers';
import { someGroupMember, USER_GROUP } from 'helpers/user';
import { GameSizeConfig } from 'components/GameItem/helpers';
import { Arrows, SliderPropsWithArrows } from './components/SliderArrows';
import { CommonUtilsContext } from 'contexts/commonUtils';

import 'swiper/swiper.min.css';
import { ScSlider, ScSliderSkeleton } from './styled';

const COEFFICIENT_FOR_REDUCING_SLIDE = 0.913;

const reducingSlide = (slidesPerView: number | 'auto' | undefined) => {
  if (typeof slidesPerView === 'number') {
    return slidesPerView / COEFFICIENT_FOR_REDUCING_SLIDE;
  }

  return slidesPerView;
};

const customParameters: CustomParameters = {
  autoplay: {
    autoplay: { disableOnInteraction: false },
    module: Autoplay,
  },
  pagination: {
    pagination: { clickable: true },
    module: Pagination,
  },
};

export enum SliderArrow {
  INSIDE,
  OUTSIDE,
}

interface SliderProps<T> extends SwiperProps {
  sliderKey?: string;
  pending?: boolean;
  pendingSlide?: React.ComponentType<{}>;
  slides?: T[];
  slide: React.ComponentType<T>;
  withArrows?: SliderPropsWithArrows;
  emptyListText?: string;
  lazyLoadingOf?: number;
  gameSizesConfig?: GameSizeConfig;
  noReduceSlidesPerView?: boolean;
  handlePrevClick?: () => void;
  handleNextClick?: () => void;
  categoryId?: string;
  getSwiperApi?: (swiper: any) => void;
}

interface SwiperState {
  isBeginning: boolean;
  isEnd: boolean;
  activeIndex: number;
}

const initialSwiperState: SwiperState = {
  isBeginning: false,
  isEnd: false,
  activeIndex: 0,
};

const initialConfig: SwiperProps = {
  spaceBetween: 16,
};

export function Slider<T>({
  pending,
  pendingSlide: PendingSlide,
  slides,
  slide: Slide,
  withArrows,
  emptyListText,
  lazyLoadingOf,
  gameSizesConfig,
  noReduceSlidesPerView,
  sliderKey,
  handlePrevClick,
  handleNextClick,
  categoryId,
  getSwiperApi,
  ...props
}: SliderProps<T>) {
  const swiperRef = useRef<any>(null);
  const { isMobile } = useContext(CommonUtilsContext);
  const { isAuthenticated, user } = useContext(AuthContext);

  const [swiperState, setSwiperState] = useState<SwiperState>(
    initialSwiperState
  );

  const handleSwiper = ({ isBeginning, isEnd, activeIndex }: SwiperState) => {
    setSwiperState({ isBeginning, isEnd, activeIndex });
  };

  const handlePrev = () => {
    handlePrevClick && handlePrevClick();
    swiperRef.current?.swiper.slidePrev();
  };

  const handleNext = () => {
    handleNextClick && handleNextClick();
    swiperRef.current?.swiper.slideNext();
  };

  const slidesPerViewSkeleton = useMemo(() => {
    if (props.slidesPerView === 'auto' || props.slidesPerView == null) return 1;
    return props.slidesPerView;
  }, [props.slidesPerView]);

  const slidesPerViewSlider = useMemo(() => {
    if (!isMobile || noReduceSlidesPerView) return props.slidesPerView;
    return reducingSlide(props.slidesPerView);
  }, [isMobile, noReduceSlidesPerView, props.slidesPerView]);

  const customProps = useMemo(() => {
    return getCustomProps(slides, customParameters, props);
  }, [props, slides]);

  const perChunk = useMemo(() => {
    if (
      lazyLoadingOf == null ||
      props?.slidesPerView == null ||
      props.slidesPerView === 'auto'
    )
      return slides?.length || 0;

    return Math.floor(props.slidesPerView) * lazyLoadingOf;
  }, [props?.slidesPerView, slides, lazyLoadingOf]);

  const { nextChunk, itemsChunk: slidesChunk } = useChunks({
    items: slides,
    perChunk,
  });

  const isPenultimate = useMemo(() => {
    return slidesChunk.length - perChunk <= swiperState.activeIndex;
  }, [slidesChunk.length, perChunk, swiperState.activeIndex]);

  useEffect(() => {
    if (isPenultimate) {
      nextChunk();
    }
  }, [isPenultimate, nextChunk]);

  const isInsideArrow = useMemo(
    () => withArrows?.type === SliderArrow.INSIDE && !isMobile,
    [withArrows?.type, isMobile]
  );

  const isNewUser = useMemo(() => {
    return !isAuthenticated || someGroupMember([USER_GROUP.SIGN_UPS])(user);
  }, [isAuthenticated, user]);

  useEffect(() => {
    getSwiperApi &&
      swiperRef?.current &&
      getSwiperApi(swiperRef.current?.swiper);
  }, [getSwiperApi]);

  return pending && PendingSlide ? (
    <ScSliderSkeleton slidesPerView={slidesPerViewSkeleton}>
      {createItems(undefined, slidesPerViewSkeleton).map((_, index) => (
        <PendingSlide key={index} />
      ))}
    </ScSliderSkeleton>
  ) : slides ? (
    <ScSlider
      slidesNumber={slidesChunk.length}
      pagination={!!props.pagination}
      isInsideArrow={isInsideArrow}
      isDesktop={!isMobile}
    >
      <Arrows
        withArrows={withArrows}
        isBeginning={swiperState.isBeginning}
        isEnd={swiperState.isEnd}
        isNewUser={isNewUser}
        onNext={handleNext}
        onPrev={handlePrev}
      />
      <Swiper
        {...initialConfig}
        {...customProps}
        slidesPerView={slidesPerViewSlider}
        onSwiper={handleSwiper}
        onSlideChange={handleSwiper}
        ref={swiperRef}
        key={sliderKey}
        {...props}
        touchStartPreventDefault={false}
        onObserverUpdate={handleSwiper}
      >
        {slidesChunk.map((slideProps: any, index) => (
          <SwiperSlide key={index}>
            <Slide
              {...slideProps}
              categoryId={categoryId}
              gameSizesConfig={gameSizesConfig}
            />
          </SwiperSlide>
        ))}
        {!slides.length && emptyListText && (
          <div className="no-result">{capitalize(emptyListText)}</div>
        )}
      </Swiper>
    </ScSlider>
  ) : null;
}
